import { Component, OnInit, OnDestroy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "./services/auth/auth.service";
import { Router } from "@angular/router";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { UtilityService } from "./services/utility.service";
import { LogoutService } from "./services/logout.service";
import { LocalStorageService } from "./services/local-storage.service";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  isCustomLayout = false;
  show: boolean = false;
  public static showLoader = true;

  timedOut = false;
  lastPing?: Date | null = null;
  IDLE_TIMEOUT = 5; // Timer starts after 5 seconds of inactivity
  INACTIVITY_TIMEOUT = 895; // User will be logged out after 15 minutes of inactivity (5s + 895s = 900s = 15min)
  PING_INTERVAL = 20;

  LOGIN_EXPIRY_TIME = 20 * 60 * 1000;

  private idleSubscriptions: Subscription[] = [];
  private authSubscription: Subscription | null = null;
  private lastActivityTime: number = Date.now();
  private backgroundTimerInterval: any = null;
  private boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);

  constructor(
    public translate: TranslateService,
    public authService: AuthService,
    public helper: UtilityService,
    private idle: Idle,
    private router: Router,
    private translateService: TranslateService,
    private logoutService: LogoutService,
    private readonly localStorage: LocalStorageService,
  ) {
    translate.setDefaultLang("en");

    this.authSubscription = this.authService.currentUserValue
      .pipe(distinctUntilChanged((prev, curr) => !!prev === !!curr))
      .subscribe((user) => {
        const isAuthenticated = !!user;
        this.show = isAuthenticated;

        if (isAuthenticated) {
          this.initializeIdleTimerConfig();
          this.reset();
          this.localStorage.setLocalStore(
            "loginTimestamp",
            Date.now().toString(),
          );
          this.setupBackgroundTimer();
        } else {
          this.idle.stop();
          this.clearBackgroundTimer();
        }
      });
  }

  private initializeIdleTimerConfig(): void {
    this.idle.stop();
    this.idle.clearInterrupts();

    this.idle.setIdle(this.IDLE_TIMEOUT);
    this.idle.setTimeout(this.INACTIVITY_TIMEOUT);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // Track activity for background timer
    this.lastActivityTime = Date.now();

    this.idleSubscriptions.forEach((sub) => sub.unsubscribe());
    this.idleSubscriptions = [
      this.idle.onIdleEnd.subscribe(() => {
        this.lastActivityTime = Date.now();
      }),
      this.idle.onTimeout.subscribe(() => {
        this.timedOut = true;
        this.idle.stop();
        this.clearBackgroundTimer();
        this.authService.logoutUser("idleLogout");
        this.helper.showInfoToast(this.translateService.instant("idleLogout"));
      }),
      this.idle.onInterrupt.subscribe(() => {
        this.lastActivityTime = Date.now();
      }),
    ];
  }

  ngOnDestroy() {
    this.idleSubscriptions.forEach((sub) => sub.unsubscribe());
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    this.idle.stop();
    this.idle.clearInterrupts();
    this.clearBackgroundTimer();
  }

  reset() {
    if (this.authService.isAuthenticated()) {
      this.idle.watch();
      this.timedOut = false;
      this.lastActivityTime = Date.now();
    } else {
      this.idle.stop();
    }
  }

  checkLoginExpiry() {
    const loginTimestamp = this.localStorage.getLocalStore("loginTimestamp");
    if (loginTimestamp) {
      const timeElapsed = Date.now() - parseInt(loginTimestamp);
      if (timeElapsed > this.LOGIN_EXPIRY_TIME) {
        this.authService.logoutUser("Session Expired. Please login again.");
      } else {
        this.localStorage.setLocalStore(
          "loginTimestamp",
          Date.now().toString(),
        );
      }
    }
  }

  async ngOnInit() {
    this.checkLoginExpiry();

    if (this.authService.isImpersonated()) {
      this.router.navigate(["/home/dashboard"]);
    } else if (!this.authService.isAuthenticated()) {
      const impersonationSuccess = await this.authService.checkImpersonation();
      if (!impersonationSuccess) {
        this.router.navigate(["/login"]);
      }
    } else {
    }
  }

  private setupBackgroundTimer(): void {
    // Clear any existing timer
    this.clearBackgroundTimer();

    // Set up visibility change listener
    document.addEventListener(
      "visibilitychange",
      this.boundHandleVisibilityChange,
    );

    // Set up interval timer that checks every 30 seconds
    this.backgroundTimerInterval = setInterval(() => {
      this.checkInactivityTimeout();
    }, 30000); // Check every 30 seconds
  }

  private clearBackgroundTimer(): void {
    if (this.backgroundTimerInterval) {
      clearInterval(this.backgroundTimerInterval);
      this.backgroundTimerInterval = null;
    }
    document.removeEventListener(
      "visibilitychange",
      this.boundHandleVisibilityChange,
    );
  }

  private handleVisibilityChange(): void {
    if (!document.hidden) {
      // Tab became visible, check if we should logout due to inactivity
      this.checkInactivityTimeout();
    }
  }

  private checkInactivityTimeout(): void {
    if (!this.authService.isAuthenticated() || this.timedOut) {
      return;
    }

    const currentTime = Date.now();
    const timeSinceLastActivity = currentTime - this.lastActivityTime;
    const maxInactivityTime =
      (this.IDLE_TIMEOUT + this.INACTIVITY_TIMEOUT) * 1000; // Convert to milliseconds

    if (timeSinceLastActivity >= maxInactivityTime) {
      this.timedOut = true;
      this.idle.stop();
      this.clearBackgroundTimer();
      this.authService.logoutUser("idleLogout");
      this.helper.showInfoToast(this.translateService.instant("idleLogout"));
    }
  }
}
